<template>
  <div>
    <v-dialog
      v-model="isCsvDialogOpen"
      width="25%"
    >
      <template v-slot:activator="{ on }">
        <v-btn v-on="on">
          <i class="fas fa-file-csv mr-2" /> Vergleich als Excel speichern
        </v-btn>
      </template>
      <v-card class="p-4">
        <h5>Export als Excel</h5>
        <v-select
          v-model="csvContractOrientation"
          :items="csvOrientationOptions"
          box
          label="Anordnung der Verträge in Excel"
        />
        <v-btn
          :disabled="isDownloading"
          :loading="isDownloading"
          class="d-block"
          @click="downloadCsv()"
        >
          <i class="fas fa-file-csv mr-2" /> Speichern
        </v-btn>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isPdfDialogOpen"
      width="25%"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          :disabled="pdfExportDisabled"
          class="d-block"
          v-on="on"
        >
          <i class="fas fa-file-pdf mr-2" /> Vergleich als PDF speichern
        </v-btn>
        <span
          v-if="loadedContracts.length > 3"
          class="ml-2 error-text"
        >
          Es können nicht mehr als 3 Verträge als PDF exportiert werden.
        </span>
      </template>
      <v-card class="p-4">
        <h4>Export als PDF</h4>
        <v-select
          v-model="pdfPageOrientation"
          :disabled="pdfOrientationDisabled"
          :items="pdfPageOrientationOptions"
          box
          label="Ausrichtung"
          @change="updatePdfSettings(loadedContracts.length)"
        />
        <v-select
          v-model="pdfPageFormat"
          :disabled="pdfFormatDisabled"
          :items="pdfPageFormatOptions"
          box
          label="Format"
        />
        <span
          v-if="shouldDisplayWarning"
          class="text-danger"
        >
          Die gewählten Einstellungen können zu einem unleserlichen PDF führen.
          Es wird empfohlen, weniger Verträge auszuwählen oder den Export als
          CSV zu verwenden.
        </span>
        <v-btn
          :disabled="isDownloading"
          :loading="isDownloading"
          class="d-block"
          @click="downloadPdf()"
        >
          <i class="fas fa-file-pdf mr-2" /> Speichern
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import pdfMake from "pdfmake";
import { GetExcelComparison } from "@/services/comparison.service.js";

export default {
  props: {
    csvDefinition: {
      type: Array,
      required: true,
    },
    pdfContent: {
      type: Array,
      required: true,
    },
    csvFileName: {
      type: String,
      default: "export.csv",
    },
    pdfFileName: {
      type: String,
      default: "export.pdf",
    },
    csvExportSettings: {
      type: Array,
      required: true,
    },
    csvTenantExportSettings: {
      type: Array,
      required: true,
    },
    loadedContracts: {
      type: Array,
      default: () => [],
    },
    selectedLanguage: {
      type: String,
      default: "de-CH",
    },
  },
  data() {
    return {
      csvOrientationOptions: ["Vertikal", "Horizontal"],
      csvContractOrientation: "Vertikal",
      pdfPageFormatOptions: ["A2", "A3", "A4"],
      pdfPageFormat: "A2",
      pdfPageOrientationOptions: ["Hochformat", "Querformat"],
      pdfPageOrientation: "Querformat",
      isDownloading: false,
      isCsvDialogOpen: false,
      isPdfDialogOpen: false,
    };
  },
  computed: {
    shouldTransposeExport() {
      return this.csvContractOrientation !== "Vertikal";
    },
    pdfExportDisabled() {
      return (
        this.loadedContracts.length !== 2 && this.loadedContracts.length !== 3
      );
    },
    pdfOrientationDisabled() {
      return (
        this.loadedContracts.length === 3 || this.loadedContracts.length < 2
      );
    },
    pdfFormatDisabled() {
      return (
        this.loadedContracts.length === 3 || this.loadedContracts.length === 2
      );
    },
    shouldDisplayWarning() {
      return (
        (this.pdfOrientation === "Hochformat" &&
          this.loadedContracts.length === 2 &&
          this.pdfPageFormat !== "A2") ||
        (this.loadedContracts.length === 3 && this.pdfPageFormat !== "A2")
      );
    },
  },
  created() {
    this.updatePdfSettings(this.loadedContracts.length);
  },
  methods: {
    updatePdfSettings(contractCount) {
      if (contractCount === 3) {
        this.pdfPageOrientation = "Querformat";
        this.pdfPageFormat = "A2";
        this.pdfPageOrientationOptions = ["Querformat"];
        this.pdfPageFormatOptions = ["A2"];
      } else if (contractCount === 2) {
        this.pdfPageOrientationOptions = ["Hochformat", "Querformat"];
        this.pdfPageFormatOptions = ["A2"];
        this.pdfPageFormat = "A2";
      } else if (contractCount !== 2 && contractCount !== 3) {
        this.pdfPageOrientationOptions = ["Hochformat", "Querformat"];
        this.pdfPageFormatOptions = ["A2", "A3", "A4"];
      }
    },
    downloadCsv() {
      this.isDownloading = true;
      let contractsToCompare = this.loadedContracts.map((contract) => ({
        contractNumber: contract.ContractInfo.contractNumber,
        versionNumber: contract.VersionInfo.number,
        versionId: contract.VersionInfo.id,
        editionId: contract.EditionInfo.id,
      }));

      GetExcelComparison(
        contractsToCompare,
        this.shouldTransposeExport,
        this.selectedLanguage,
        (response) => {
          const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${
            response.fileBase64String
          }`;
          const downloadLink = document.createElement("a");
          const fileName = response.filename;

          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
          this.isDownloading = false;
          this.isCsvDialogOpen = false;
        },
        (error) => {
          console.error(error);
          this.isDownloading = false;
        },
      );
    },
    downloadPdf() {
      if (this.pdfExportDisabled) return;

      this.isDownloading = true;
      let docDefinition = {
        pageOrientation: this.pdfOrientation,
        pageSize: this.pdfPageFormat,
        content: [
          {
            text: "Vertragsvergleich",
            style: "header",
          },
          {
            table: {
              body: this.pdfContent,
            },
          },
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 10],
          },
        },
        defaultStyle: {
          fontSize: 12,
        },
      };

      pdfMake.createPdf(docDefinition).download(this.pdfFileName);
      this.isDownloading = false;
      this.isPdfDialogOpen = false;
    },
  },
};
</script>

<style scoped>
.error-text {
  color: #ff0000;
}
</style>
